import { Button, Text } from '@lbcamden/lib-react';
import { Root, Portal, Title, Description } from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { FunctionComponent, useEffect } from 'react';
import { Fade } from 'transitions-kit';

import { webAuthnEnabled } from '~/client/config/appConfig';
import { useClientPinAuthInfo, useHandleAuth, useIsUserRegistered } from '~/client/hooks/authHooks';
import { useCurrentUser } from '~/client/hooks/storeHooks';
import { useDetectPageHidden } from '~/client/hooks/useDetectPageHidden';
import { logout } from '~/client/lib/auth';
import { useAppStore } from '~/client/store/app/appStore';

import { PageTitle } from '../../common/PageTitle';
import { ResetPin, UserPinAuth } from '../UserPinAuth';
import { OverlayContent, WebAuthnOverlay, PinAuthOverlay } from './ClientAuthOverlay.styled';

const testId = 'client-auth-overlay';
const title = webAuthnEnabled ? 'Auth required' : 'PIN required';

export const ClientAuthOverlay: FunctionComponent = () => {
  const pageHidden = useDetectPageHidden();

  const { hasPin } = useClientPinAuthInfo();

  const currentUser = useCurrentUser();
  const needsClientAuth = useAppStore((state) => state.needsClientAuth);

  const { isRegistered = false } = useIsUserRegistered();
  const { handleClientAuth: handleWebAuthn } = useHandleAuth({
    onAuthFailure: logout,
  });

  const shouldAuth = !!currentUser && isRegistered && needsClientAuth && !pageHidden;
  const needsStoredPin = !webAuthnEnabled && !hasPin;

  useEffect(() => {
    if (shouldAuth && webAuthnEnabled) {
      void handleWebAuthn();
    }
  }, [shouldAuth]);

  useEffect(() => {
    // Cannot perform client auth. User needs to auth with server first
    if (shouldAuth && needsStoredPin) {
      void logout();
    }
  }, [shouldAuth]);

  // Makes use of @radix-ui/react-dialog to ensure the overlay works correctly when a dialog is open
  return (
    <Root open={shouldAuth}>
      {shouldAuth && <PageTitle title={title} />}

      <Portal forceMount>
        <Fade in={shouldAuth} unmountOnExit>
          <OverlayContent>
            <VisuallyHidden>
              <Title>{title}</Title>
              <Description>Authentication is required in order to continue.</Description>
            </VisuallyHidden>

            {webAuthnEnabled ? (
              <WebAuthnOverlay data-testid={testId} />
            ) : (
              <PinAuthOverlay
                data-testid={testId}
                footerContent={
                  <>
                    <ResetPin />
                    <Button $variant="tertiary" $color="primary" onClick={logout}>
                      Sign out
                    </Button>
                  </>
                }
              >
                <Text variant="h1">PIN required</Text>
                <Text variant="lead">Enter your PIN to continue</Text>
                <UserPinAuth isClientAuth onUnexpectedError={logout} />
              </PinAuthOverlay>
            )}
          </OverlayContent>
        </Fade>
      </Portal>
    </Root>
  );
};
